import { Component } from '@theme/component';
import { ThemeEvents, ProductSummaryUpdateEvent, ProductDeliveryUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} total - The order total
 * @property {HTMLElement} delivery - The copy of the delivery line
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows while neither the product form nor the back-in-stock prompt is in view, and repeats the order summary's
 * total and the delivery line from the events they announce.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'total', 'delivery'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {ResizeObserver | null} */
  #pageResizeObserver = null;

  /** @type {number} The page wrapper's last measured width */
  #pageWidth = 0;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {Set<Element>} The watched purchase controls currently in view */
  #visibleTargets = new Set();

  /** @type {boolean} */
  #isFooterInView = false;

  /** @type {boolean} Whether the next cart update is the add this bar started */
  #isAwaitingAdd = false;

  /** @type {number | null} The order summary's total in minor units, once it has announced one */
  #total = null;

  /** @type {string[]} */
  #deliveryParts = [];

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener(ThemeEvents.productSummaryUpdate, this.#handleSummaryUpdate, { signal });
    target?.addEventListener(ThemeEvents.productDeliveryUpdate, this.#handleDeliveryUpdate, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });

    // A drawer squeezes the page without resizing the window; height changes, such as Safari's toolbar, are ignored
    this.#pageResizeObserver = new ResizeObserver(([entry]) => {
      if (!entry || entry.contentRect.width === this.#pageWidth) return;

      this.#pageWidth = entry.contentRect.width;
      this.#measure();
    });
    this.#pageResizeObserver.observe(document.querySelector('.page-wrapper') ?? document.documentElement);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#pageResizeObserver?.disconnect();
    this.#abortController.abort();
  }

  /**
   * Watches the product form and the back-in-stock prompt, whichever of the two the details column shows, and the
   * footer.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#visibleTargets.add(entry.target);
        } else {
          this.#visibleTargets.delete(entry.target);
        }
      }

      this.#updateVisibility();
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        this.#isFooterInView = entry.isIntersecting;
        this.#updateVisibility();
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    const notifyMe = this.closest('.shopify-section')?.querySelector('product-notify-me');
    if (notifyMe) this.#buyButtonsIntersectionObserver.observe(notifyMe);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Adds to cart through the product form, so add-ons, the selling plan and a personalization travel with it, or
   * opens the back-in-stock dialog.
   */
  handleAddToCartClick = () => {
    if (this.dataset.state === 'notify') {
      /** @type {HTMLElement | null | undefined} */
      const notifyMeButton = this.closest('.shopify-section')?.querySelector('.product-notify-me__button');
      notifyMeButton?.click();
      return;
    }

    if (!this.#targetAddToCartButton) return;

    this.#isAwaitingAdd = true;
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        // A morphed element, such as `<main>` for a combined listing sibling, already carries the new bar
        if (detail.morphElementSelector) {
          this.#total = null;
          this.#deliveryParts = [];
          this.#targetAddToCartButton =
            this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? this.#targetAddToCartButton;
          return;
        }

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.state = newStickyAddToCart.dataset.state ?? '';

        // Update the dataset attributes with new variant info
        this.dataset.currentVariantId = variant?.id ?? '';

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        // The server renders the variant's price and no delivery line; the summary and the line announce theirs
        this.#renderTotal();
        this.#renderDelivery();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * @param {ProductSummaryUpdateEvent} event - The product summary update event.
   */
  #handleSummaryUpdate = (event) => {
    this.#total = event.detail.total;
    this.#renderTotal();
  };

  /**
   * @param {ProductDeliveryUpdateEvent} event - The product delivery update event.
   */
  #handleDeliveryUpdate = (event) => {
    this.#deliveryParts = event.detail.parts;
    this.#renderDelivery();
  };

  #renderTotal() {
    if (this.#total === null) return;

    this.refs.total.textContent = formatMoney(
      this.#total,
      this.dataset.moneyFormat ?? '{{amount}}',
      this.dataset.currency ?? ''
    );
  }

  #renderDelivery() {
    const { delivery } = this.refs;

    delivery.replaceChildren(
      ...this.#deliveryParts.map((content) => {
        const part = document.createElement('span');
        part.className = 'sticky-add-to-cart__delivery-part';
        part.textContent = content;
        return part;
      })
    );
    delivery.hidden = this.#deliveryParts.length === 0;
  }

  /**
   * Keeps the bar's button busy until the add it started settles.
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // A validation stop leaves the form's add unsent, so an update from elsewhere must not count as it
    const isFromProductForm = event.target instanceof Node && Boolean(this.#getProductForm()?.contains(event.target));
    const isOwnAdd = this.#isAwaitingAdd && isFromProductForm;
    if (isFromProductForm) this.#isAwaitingAdd = false;

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      const { addToCartButton } = this.refs;
      if (isOwnAdd) addToCartButton.setAttribute('aria-busy', 'true');

      event.promise
        .catch(() => {})
        .finally(() => {
          if (isOwnAdd) addToCartButton.removeAttribute('aria-busy');
        });
    }
  };

  #updateVisibility() {
    if (this.#visibleTargets.size === 0 && !this.#isFooterInView) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#measure();
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Hands the order summary's width and edge to the styles, which line the bar up with it on wider screens.
   */
  #measure = () => {
    const summary = this.closest('.shopify-section')?.querySelector('product-summary');
    if (!summary) return;

    const { left, right, width } = summary.getBoundingClientRect();
    const isRightToLeft = getComputedStyle(this).direction === 'rtl';
    const insetInlineEnd = isRightToLeft ? left : document.documentElement.clientWidth - right;

    this.style.setProperty('--sticky-bar-width', `${width}px`);
    this.style.setProperty('--sticky-bar-inset-inline-end', `${insetInlineEnd}px`);
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
